import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  List,
  ListItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ExclamationCircleIcon,
  InfoCircleIcon,
  MagicIcon,
  ShieldAltIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useMailDomainDmarc } from '../../../hooks/useMailDomainDmarc';
import { parseDmarc } from './dmarcParse';
import DmarcWizardModal from './DmarcWizardModal';

interface DmarcTabProps {
  mailDomainId: number;
  organizationDomainId: number;
  domainName: string;
}

/**
 * Shows the DMARC policy published for a mail domain at its `_dmarc` label: the raw record string, its
 * decomposition into the requested policy + reporting addresses + a tag-by-tag breakdown (via
 * {@link parseDmarc}), and advisories for common misconfigurations.
 *
 * Owns its own best-effort live DNS read via {@link useMailDomainDmarc}, so it must be mounted lazily — the
 * parent `<Tabs>` sets `mountOnEnter` and this tab provides a Refresh action for manual re-runs. A DNS read
 * that fails is not a request error: `dmarc_state` is `'unknown'` and the backend's safe `dmarc_error` is
 * surfaced. The "Build DMARC record" button opens the guided builder wizard.
 */
function DmarcTab({ mailDomainId, organizationDomainId, domainName }: DmarcTabProps) {
  const { data, loading, error, refetch } = useMailDomainDmarc(mailDomainId);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(
    () => (data?.dmarc_state === 'configured' && data.value ? parseDmarc(data.value) : null),
    [data],
  );

  const primaryRecord = data?.records[0] ?? null;
  const recordName = data ? `_dmarc.${data.domain}` : '';

  const onCopy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const renderToolbar = () => (
    <Toolbar>
      <ToolbarContent>
        <ToolbarItem>
          <Button variant="primary" icon={<MagicIcon />} onClick={() => setIsWizardOpen(true)}>
            Build DMARC record
          </Button>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="secondary" icon={<SyncAltIcon />} onClick={() => refetch()} isLoading={loading}>
            Refresh
          </Button>
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderConfigured = () => {
    if (!data) return null;

    const advisories = parsed?.warnings ?? [];
    // Tags shown in the breakdown table — the version tag is shown separately as "DMARC1".
    const breakdownTags = parsed?.tags.filter((t) => t.name !== 'v') ?? [];

    return (
      <>
        {data.has_multiple && (
          <Alert
            variant="danger"
            isInline
            title="Multiple DMARC records are published"
            className="pf-v6-u-mb-md"
          >
            A domain may publish only one DMARC record at <code>{recordName}</code>. With more than one,
            receivers cannot tell which policy applies and ignore DMARC entirely. Merge them into a single
            record.
          </Alert>
        )}

        {parsed && !parsed.valid && (
          <Alert variant="warning" isInline title="Could not interpret the record" className="pf-v6-u-mb-md">
            The published value does not look like a valid DMARC record. The raw value is shown below.
          </Alert>
        )}

        {advisories.length > 0 && (
          <Alert variant="warning" isInline title="Recommendations" className="pf-v6-u-mb-md">
            {advisories.length === 1 ? (
              advisories[0]
            ) : (
              <List>
                {advisories.map((w, i) => (
                  <ListItem key={i}>{w}</ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}

        <Card>
          <CardTitle component="h2">Published DMARC record</CardTitle>
          <CardBody>
            <CodeBlock
              actions={
                <CodeBlockAction>
                  <ClipboardCopyButton
                    id="dmarc-copy-button"
                    aria-label="Copy DMARC record to clipboard"
                    variant="plain"
                    onClick={() => onCopy(data.value ?? '')}
                    exitDelay={copied ? 1500 : 600}
                    maxWidth="110px"
                    onTooltipHidden={() => setCopied(false)}
                  >
                    {copied ? 'Copied!' : 'Copy to clipboard'}
                  </ClipboardCopyButton>
                </CodeBlockAction>
              }
            >
              <CodeBlockCode>{data.value}</CodeBlockCode>
            </CodeBlock>

            <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-md">
              <DescriptionListGroup>
                <DescriptionListTerm>Record</DescriptionListTerm>
                <DescriptionListDescription>
                  <code>{recordName}</code>
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Record type</DescriptionListTerm>
                <DescriptionListDescription>TXT record</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>TTL</DescriptionListTerm>
                <DescriptionListDescription>
                  {primaryRecord ? `${primaryRecord.ttl} seconds` : '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>

        {parsed?.valid && (
          <Card className="pf-v6-u-mt-md">
            <CardTitle component="h2">Decomposition</CardTitle>
            <CardBody>
              <Content component="h3" className="pf-v6-u-font-size-sm pf-v6-u-mb-xs">
                Policy
              </Content>
              {parsed.policyInfo ? (
                <>
                  <Label status={parsed.policyInfo.severity}>{parsed.policyInfo.label}</Label>
                  <Content component="p" className="pf-v6-u-mt-sm">
                    {parsed.policyInfo.description}
                  </Content>
                </>
              ) : (
                <Content component="p">
                  This record specifies no <code>p=</code> policy, so receivers ignore it (no protection).
                </Content>
              )}
              {parsed.subdomainPolicy && (
                <Content component="p" className="pf-v6-u-mt-sm">
                  Subdomains use their own policy: <code>sp={parsed.subdomainPolicy}</code>.
                </Content>
              )}
              {parsed.pct !== null && parsed.pct < 100 && (
                <Content component="p" className="pf-v6-u-mt-sm">
                  The policy is applied to <strong>{parsed.pct}%</strong> of mail; the rest is treated as{' '}
                  <code>p=none</code>.
                </Content>
              )}

              <Content component="h3" className="pf-v6-u-font-size-sm pf-v6-u-mt-lg pf-v6-u-mb-sm">
                Reporting
              </Content>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>Aggregate reports (rua)</DescriptionListTerm>
                  <DescriptionListDescription>
                    {parsed.rua.length > 0 ? (
                      <List isPlain>
                        {parsed.rua.map((u, i) => (
                          <ListItem key={i}>
                            <code style={{ wordBreak: 'break-all' }}>{u}</code>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      'None — no aggregate reports will be received.'
                    )}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Failure reports (ruf)</DescriptionListTerm>
                  <DescriptionListDescription>
                    {parsed.ruf.length > 0 ? (
                      <List isPlain>
                        {parsed.ruf.map((u, i) => (
                          <ListItem key={i}>
                            <code style={{ wordBreak: 'break-all' }}>{u}</code>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      'None'
                    )}
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>

              <Content component="h3" className="pf-v6-u-font-size-sm pf-v6-u-mt-lg pf-v6-u-mb-sm">
                Record breakdown
              </Content>
              {breakdownTags.length > 0 ? (
                <Table aria-label="DMARC record tags" variant="compact">
                  <Thead>
                    <Tr>
                      <Th width={15}>Tag</Th>
                      <Th width={25}>Value</Th>
                      <Th>Meaning</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {breakdownTags.map((t, i) => (
                      <Tr key={i}>
                        <Td dataLabel="Tag">
                          <code>{t.name}</code>
                        </Td>
                        <Td dataLabel="Value">
                          <code style={{ wordBreak: 'break-all' }}>{t.value}</code>
                        </Td>
                        <Td dataLabel="Meaning" modifier="wrap">
                          {t.description}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Content component="p">This record carries only the version tag.</Content>
              )}
            </CardBody>
          </Card>
        )}
      </>
    );
  };

  const renderBody = () => {
    // Initial load only — a refetch keeps the existing view visible and surfaces progress on the
    // Refresh button instead of replacing the whole tab with a spinner.
    if (loading && !data) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load DMARC record"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!data) return null;

    if (data.dmarc_state === 'unknown') {
      // No toolbar here: DMARC can't be published into a zone that isn't managed, so "Build" is hidden and
      // the only action — Refresh, to re-attempt the DNS read — lives in the empty-state footer (SPF pattern).
      return (
        <EmptyState titleText="DMARC status unavailable" icon={InfoCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            {data.dmarc_error ?? 'The DMARC record could not be determined for this domain.'}
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" icon={<SyncAltIcon />} onClick={() => refetch()} isLoading={loading}>
                Refresh
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (data.dmarc_state === 'not_configured') {
      return (
        <>
          {renderToolbar()}
          <EmptyState titleText="No DMARC record published" icon={ShieldAltIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              No DMARC policy is published at <strong>{recordName}</strong>. Publishing one builds on SPF and
              DKIM: it tells receiving servers how to handle mail that fails authentication and where to send
              reports, which helps prevent spoofing.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" icon={<MagicIcon />} onClick={() => setIsWizardOpen(true)}>
                  Build DMARC record
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </>
      );
    }

    return (
      <>
        {renderToolbar()}
        {renderConfigured()}
      </>
    );
  };

  return (
    <>
      {renderBody()}
      <DmarcWizardModal
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        organizationDomainId={organizationDomainId}
        domainName={domainName}
        currentValue={data?.value ?? null}
        onPublished={() => void refetch()}
      />
    </>
  );
}

export default DmarcTab;
